import React from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  getPaginationRowModel,
  ColumnDef,
  getExpandedRowModel,
} from "@tanstack/react-table";

import styles from "./table.module.scss";
import { Loading } from "../../atoms/loading";
import classNames from "classnames";

import NO_TRANSACTION_IMAGE from "assets/document-file-not-found-search-no-result-concept-poolban.png";
type TableProps<TData> = {
  data?: TData[];
  isLoading?: boolean;
  columns: ColumnDef<TData>[];
  getRowCanExpand?: () => boolean;
  containerClass?: string;
  tableClass?: string;
};

export const Table: React.FC<TableProps<any>> = ({
  columns,
  data = [],
  isLoading = false,
  containerClass,
  tableClass,
  getRowCanExpand,
}) => {
  const table = useReactTable({
    columns,
    data,
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });
  const tableClassName = classNames(styles.table, tableClass);
  const containerClassName = classNames(styles.table_container, containerClass);
  return (
    <div className={containerClassName}>
      <table className={tableClassName}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: " 🔼",
                          desc: " 🔽",
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {!isLoading &&
            table.getRowModel().rows.map((row, index) => {
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={
                      Number(row.id) % 2 === 0
                        ? styles.row_background_1
                        : styles.row_background_2
                    }
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr
                      className={
                        Number(row.id) % 2 === 0
                          ? styles.row_background_1
                          : styles.row_background_2
                      }
                      key={row.id + "expanded"}
                    >
                      <td
                        colSpan={row.getVisibleCells().length}
                        className={styles.sub_row_table_container}
                      >
                        <div className={styles.sub_row_table}>
                          <Table
                            columns={columns}
                            data={row.original.subRows}
                            key={row.id + "expanded"}
                            containerClass={containerClassName}
                            tableClass={tableClass}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
      {!isLoading && !data.length && (
        <div className="w-100 p-5 flex-center align-items-center flex-column">
          <img
            className="col-2"
            src={NO_TRANSACTION_IMAGE}
            alt="No Transactions found"
          />
        </div>
      )}
      {isLoading && (
        <div className={classNames(styles.loading, "flex-center")}>
          <Loading />
        </div>
      )}
    </div>
  );
};
